<template>
  <div class="visit-expenses-view">
    <div class="visit-expenses-view__header-wrapper">
      <header class="page__header--container clearfix">
        <h1 class="page__title--text pull-left">
          VISIT INSPECTION EXPENSES
          <small v-if="tripId"> TRIP ID: {{ tripId }} </small>
        </h1>
      </header>
      <div class="visit-expenses-view__navigation">
        <Button
          v-if="!isEmailLink"
          type="button"
          theme-color="dark"
          @click="onPreviousClicked"
          data-cy="previous-button"
          >Previous</Button
        >
        <Button
          type="button"
          theme-color="dark"
          @click="onNextClicked"
          data-cy="next-button"
          :disabled="tripVisits.length == 0"
          >Next</Button
        >
      </div>
    </div>
    <div class="visit-expenses-view__content">
      <div
        v-if="!loadingTripVisits && tripVisits.length == 0"
        class="visit-expenses-view__error"
      >
        There are no visits selected for this Trip. You must select at least one
        visit to submit expenses for a trip. Press the 'Previous' button above
        to return to your list of visits and select one or more visits.
      </div>
      <TripVisitsGrid
        :tripId="tripId"
        :tripVisits="tripVisits"
        :loading="loadingTripVisits"
        @visitDeleted="onVisitDeleted"
      />
      <VisitExpensesForm
        :tripId="tripId"
        :submitAvailable="tripVisits.length > 0"
        ref="expensesFormRef"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { EnumVisitMode } from "@/Domain/EnumVisitMode";
import { Button } from "@progress/kendo-vue-buttons";
import { onBeforeMount, ref } from "vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { TripVisitDisplay } from "@/Domain/TripVisitDisplay";
import TripVisitsGrid from "./TripVisitsGrid.vue";
import VisitExpensesForm from "./VisitExpensesForm.vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const urlParams = new URLSearchParams(window.location.search);
const tripId = ref(0);
const tripVisits = ref<TripVisitDisplay[]>([]);
const expensesFormRef = ref<typeof VisitExpensesForm>();
const loadingTripVisits = ref(false);

//const formMode = urlParams.get("mode") as "new" | "view";

const menuUserRole = (urlParams.get("menu") as string) ?? "VisitsAsRfr";
const type =
  EnumVisitMode[urlParams.get("type") as keyof typeof EnumVisitMode] ??
  EnumVisitMode.Inspection;
const isEmailLink = urlParams.get("emailLink")?.toLowerCase() == "y";

const onPreviousClicked = async () => {
  await expensesFormRef.value?.saveExpensesAsync();
  window.location.href = `RfrVisitsSearch?type=${
    EnumVisitMode[type]
  }&menu=${menuUserRole}`;
};

const onNextClicked = () => {
  expensesFormRef.value?.saveExpensesAsync(true);
};

const getTripIdAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("GetTripId", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
    }),
    (data) => {
      tripId.value = data;
    },
    "VisitInspectionExpensesView.vue",
    "getTripIdAsync"
  );

  return tripId;
};

const getTripVisitsAsync = async function () {
  try {
    if (!tripId.value) return;
    loadingTripVisits.value = true;
    await handleResponsePromiseAsync(
      api.get("GetTripVisits", {
        tripId: tripId.value,
        newTrip: true,
      }),
      (data) => {
        tripVisits.value = data;
      },
      "VisitInspectionExpensesView.vue",
      "getTripVisitsAsync"
    );
  } finally {
    loadingTripVisits.value = false;
  }
};

const onVisitDeleted = (visitId: number) => {
  tripVisits.value = tripVisits.value.filter((v) => v.VisitId !== visitId);
};

onBeforeMount(async () => {
  loadingTripVisits.value = true;
  await getTripIdAsync();
  if (!tripId.value) {
    notificationStore.notify({
      message: "Trip not found",
      type: NotificationTypeEnum.error,
      duration: 5000,
    });
    loadingTripVisits.value = false;
    return;
  }
  getTripVisitsAsync();
});
</script>

<style scoped lang="scss">
.visit-expenses-view {
  padding: 20px;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: 900px;
  width: 100%;

  &__header-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-flow: wrap-reverse;
  }

  &__error {
    background-color: #f8d7da;
    border-color: #f5c2c7;
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 4px;
  }

  &__navigation {
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}
</style>
